import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PendingReward, claimAllRewards, getRewardTypeName } from '../../services/referralService';
import './TokenRewardAnimation.css';

interface TokenRewardAnimationProps {
    pendingRewards: PendingReward[];
    onComplete: (newBalance: number | null) => void;
    onClose: () => void;
}

const TokenRewardAnimation: React.FC<TokenRewardAnimationProps> = ({
    pendingRewards,
    onComplete,
    onClose,
}) => {
    const { t, i18n } = useTranslation('tokens');
    const [, setIsAnimating] = useState(false);
    const [currentRewardIndex, setCurrentRewardIndex] = useState(-1);
    const [showTotal, setShowTotal] = useState(false);
    const [totalClaimed, setTotalClaimed] = useState(0);
    const [isClaiming, setIsClaiming] = useState(false);

    const totalAmount = pendingRewards.reduce((sum, r) => sum + r.amount, 0);

    const startClaim = useCallback(async () => {
        setIsClaiming(true);
        setIsAnimating(true);

        try {
            const result = await claimAllRewards();
            setTotalClaimed(result.totalClaimed);

            // Animate through each reward
            for (let i = 0; i < pendingRewards.length; i++) {
                setCurrentRewardIndex(i);
                await new Promise(resolve => setTimeout(resolve, 800));
            }

            // Show total
            setShowTotal(true);
            await new Promise(resolve => setTimeout(resolve, 2000));

            onComplete(result.newBalance);
        } catch (error) {
            console.error('Failed to claim rewards:', error);
            onClose();
        } finally {
            setIsClaiming(false);
        }
    }, [pendingRewards, onComplete, onClose]);

    useEffect(() => {
        // Auto-start claim animation after a short delay
        const timer = setTimeout(() => {
            startClaim();
        }, 500);

        return () => clearTimeout(timer);
    }, [startClaim]);

    const lang = i18n.language as 'zh-TW' | 'en';

    return (
        <div className="token-reward-overlay">
            <div className="token-reward-modal">
                {/* Close button */}
                <button className="token-reward-close" onClick={onClose} disabled={isClaiming}>
                    <span>&times;</span>
                </button>

                {/* Header */}
                <div className="token-reward-header">
                    <div className="token-reward-icon">
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="10" fill="#FFD700" />
                            <path
                                d="M12 6v12M8 10l4-4 4 4M8 14l4 4 4-4"
                                stroke="#B8860B"
                                strokeWidth="2"
                                strokeLinecap="round"
                            />
                        </svg>
                    </div>
                    <h2>{t('reward.congratulations', 'Congratulations!')}</h2>
                    <p>{t('reward.pendingRewardsTitle', 'You have referral rewards to claim!')}</p>
                </div>

                {/* Rewards list */}
                <div className="token-reward-list">
                    {pendingRewards.map((reward, index) => (
                        <div
                            key={reward.referralId + reward.type}
                            className={`token-reward-item ${
                                index <= currentRewardIndex ? 'claimed' : ''
                            } ${index === currentRewardIndex ? 'animating' : ''}`}
                        >
                            <div className="reward-type">
                                {getRewardTypeName(reward.type, lang)}
                                {reward.referrerUsername && (
                                    <span className="reward-from">
                                        {reward.type === 'signup'
                                            ? t('reward.fromInviter', 'from {{name}}', { name: reward.referrerUsername })
                                            : t('reward.friendSubscribed', '{{name}} subscribed', { name: reward.referrerUsername })}
                                    </span>
                                )}
                            </div>
                            <div className="reward-amount">
                                <span className="token-icon">T</span>
                                <span className="amount">+{reward.amount}</span>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Total */}
                {showTotal && (
                    <div className="token-reward-total show">
                        <div className="total-label">{t('reward.totalEarned', 'Total Earned')}</div>
                        <div className="total-amount">
                            <span className="token-icon large">T</span>
                            <span className="amount">+{totalClaimed || totalAmount}</span>
                        </div>
                    </div>
                )}

                {/* Action button */}
                <button
                    className={`token-reward-button ${showTotal ? 'show' : ''}`}
                    onClick={onClose}
                    disabled={!showTotal}
                >
                    {t('reward.awesome', 'Awesome!')}
                </button>
            </div>
        </div>
    );
};

export default TokenRewardAnimation;
